import { useCallback, useEffect, useState } from 'react';

import { useLocalStorage } from '@/hooks/use-local-storage';
import { NotificationPermissionState } from '@/modules/notifications';
import { logger } from '@/utils/logger/logger';

import notificationService from '../firebase-notification.service';

const TAG = 'useNotificationPermission:';

const NOTIFICATION_PERMISSION_DISMISSED_AT_STORAGE_KEY = 'notification_permission_dismissed_at';
const NOTIFICATION_DENIED_REMINDED_AT_STORAGE_KEY = 'notification_denied_reminded_at';

const REMINDER_HOUR = 9;
const DENIED_REMINDER_INTERVAL_DAYS = 7;

const shouldShowAfterDismissal = (dismissedAt: string | null): boolean => {
  if (!dismissedAt) return true;

  const dismissedDate = new Date(dismissedAt);
  if (isNaN(dismissedDate.getTime())) return true;

  const nextReminder = new Date(dismissedDate);
  nextReminder.setDate(nextReminder.getDate() + 1);
  nextReminder.setHours(REMINDER_HOUR, 0, 0, 0);

  return new Date() >= nextReminder;
};

const shouldShowDeniedReminder = (remindedAt: string | null): boolean => {
  if (!remindedAt) return true;

  const remindedDate = new Date(remindedAt);
  if (isNaN(remindedDate.getTime())) return true;

  const nextReminder = new Date(remindedDate);
  nextReminder.setDate(nextReminder.getDate() + DENIED_REMINDER_INTERVAL_DAYS);
  nextReminder.setHours(REMINDER_HOUR, 0, 0, 0);

  return new Date() >= nextReminder;
};

export const useNotificationPermission = () => {
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showDeniedModal, setShowDeniedModal] = useState(false);
  const [dismissedAt, setDismissedAt] = useLocalStorage<string | null>(
    NOTIFICATION_PERMISSION_DISMISSED_AT_STORAGE_KEY,
    null
  );
  const [deniedRemindedAt, setDeniedRemindedAt] = useLocalStorage<string | null>(
    NOTIFICATION_DENIED_REMINDED_AT_STORAGE_KEY,
    null
  );

  const initializeNotifications = useCallback(async () => {
    try {
      logger.debug(TAG, 'Initializing Firebase notifications');
      await notificationService.requestPermissionAndInitialize();
      logger.info(TAG, 'Firebase notifications initialized successfully');
    } catch (error) {
      logger.error(TAG, 'Failed to initialize Firebase notifications', error);
    }
  }, []);

  useEffect(() => {
    const permissionState = notificationService.checkPermissionState();

    if (permissionState === NotificationPermissionState.PROCEED) {
      initializeNotifications();
      return;
    }

    if (
      permissionState === NotificationPermissionState.DENIED &&
      shouldShowDeniedReminder(deniedRemindedAt)
    ) {
      setShowDeniedModal(true);
      return;
    }

    if (
      permissionState === NotificationPermissionState.PROMPT &&
      shouldShowAfterDismissal(dismissedAt)
    ) {
      setShowPermissionModal(true);
    }
  }, [initializeNotifications, dismissedAt, deniedRemindedAt]);

  const handleEnableNotifications = useCallback(async () => {
    setShowPermissionModal(false);
    await initializeNotifications();

    // User clicked "Enable" but denied the browser prompt
    if (Notification.permission === 'denied') {
      setShowDeniedModal(true);
    }
  }, [initializeNotifications]);

  const handleDismissPermissionModal = useCallback(() => {
    setShowPermissionModal(false);
    setDismissedAt(new Date().toISOString());
  }, [setDismissedAt]);

  const handleDismissDeniedModal = useCallback(() => {
    setShowDeniedModal(false);
    setDeniedRemindedAt(new Date().toISOString());
  }, [setDeniedRemindedAt]);

  return {
    showPermissionModal,
    showDeniedModal,
    handleEnableNotifications,
    handleDismissPermissionModal,
    handleDismissDeniedModal,
  };
};
